import type { Settlement as GQLItem } from '../../../generated/api.graphql';
import * as Address from '../Address/Address';
import * as CustomExpense from './CustomExpense';
import * as Deal from './Deal';
import * as Event from './Event';
import * as ManualTicketSale from './ManualTicketSale';
import * as Payment from './Payment';
import * as SettlementTypeEnum from './SettlementType';
import * as TaxWithholdingType from './TaxWithholdingType';

/**
 * An artist settlement with its deals, payments, expenses and tax detail.
 *
 * Nullability mirrors the schema, as it does everywhere else under `App/`: only `id` is non-null.
 * `address` is a single object here, not the array the admin store's local `eventArtistSettlement`
 * interface declared and no template ever read it as.
 *
 * `printableUrl` is the always-resolving printable sheet the "Settlement Sheet" link opens;
 * `pdfDownloadUrl` is `attachment&.url`, nil until `generateSettlementPdf` has run.
 *
 * The collections default to `[]` rather than staying optional: every consumer indexes them
 * unconditionally, and "no rows" must not have to be told apart from "the payload did not carry
 * them".
 */
export type Settlement = {
  id: number;
  createdAt?: string;
  pdfDownloadUrl?: string;
  printableUrl?: string;
  settlementType?: SettlementTypeEnum.SettlementType;
  businessName?: string;
  taxId?: string;
  taxWithholdingAmount?: number;
  taxWithholdingType?: TaxWithholdingType.TaxWithholdingType;
  taxLabel?: string;
  eventIds: number[];
  address?: Address.Address;
  availableEvents: Event.SettlementEvent[];
  events: Event.SettlementEvent[];
  barDeals: Deal.SettlementDeal[];
  ticketDeals: Deal.SettlementDeal[];
  customExpenses: CustomExpense.CustomExpense[];
  manualTicketSales: ManualTicketSale.ManualTicketSale[];
  payments: Payment.Payment[];
};

export const fromGql = (item: GQLItem): Settlement => ({
  id: item.id,
  createdAt: item.createdAt ?? undefined,
  pdfDownloadUrl: item.pdfDownloadUrl ?? undefined,
  printableUrl: item.printableUrl ?? undefined,
  settlementType: item.settlementType ?? undefined,
  businessName: item.businessName ?? undefined,
  taxId: item.taxId ?? undefined,
  taxWithholdingAmount: item.taxWithholdingAmount ?? undefined,
  taxWithholdingType: item.taxWithholdingType ?? undefined,
  taxLabel: item.taxLabel ?? undefined,
  eventIds: item.eventIds ?? [],
  address: item.address ? Address.fromGql(item.address) : undefined,
  availableEvents: (item.availableEvents ?? []).map(Event.fromGql),
  events: (item.events ?? []).map(Event.fromGql),
  barDeals: (item.barDeals ?? []).map(Deal.fromGql),
  ticketDeals: (item.ticketDeals ?? []).map(Deal.fromGql),
  customExpenses: (item.customExpenses ?? []).map(CustomExpense.fromGql),
  manualTicketSales: (item.manualTicketSales ?? []).map(ManualTicketSale.fromGql),
  payments: (item.payments ?? []).map(Payment.fromGql),
});
